/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
    func, object, bool, string,
} from 'prop-types';
import Paper from '@material-ui/core/Paper';
import {
    MenuList, TextField, InputAdornment, IconButton,
} from '@material-ui/core';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import { useUIDSeed } from 'react-uid';
import SendIcon from '@material-ui/icons/Send';
import { getCountryCodeByCategoryPath } from '../../../../state/ducks/Checkout/helpers/countryCode';
import useDebounce from '../../../helpers/hooks/useDebounce';
import { getSuggestions } from '../utils/AigoWidgetHelper';
import inputSanitization from '../utils/inputHelper';
import QasClient from '../../../../apis/qas-address-search-apis/qas-address-search-apis';

const AVAILABLE_COUNTRY_CODES = ['united states', 'canada'];

const ChatInput = ({
    onSubmitMessage,
    classList,
    isChatInputDisabled,
    setIsTyping,
    lastMessageIndex,
    featureFlags,
    forwardRef,
    chatWidgetMinimized,
    convId,
}) => {
    // states
    const [message, setMessage] = useState('');
    const [addressList, setAddressList] = useState();
    const [showAddressSuggestion, setShowAddressSuggestion] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    // refs
    const isTypingInterval = useRef();
    const chatAddressRef = useRef();
    // selectors
    const country = useSelector((state) => state.ui.country) || 'united states';
    const seed = useUIDSeed();
    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    /**
     * Show user typing indicator every 2 seconds while the user
     * keeps typing a message
     */
    const updateIsTyping = () => {
        if (isTypingInterval.current) {
            clearTimeout(isTypingInterval.current);
        }
        setIsTyping(true);
        isTypingInterval.current = setTimeout(() => setIsTyping(false), 2000);
    };
    /**
     * Clean address search list
     */
    const resetAutoSearch = () => {
        setAddressList();
    };
    /**
     * Restore the focus to chat input
     */
    const restoreChatFocus = () => {
        if (chatAddressRef?.current) chatAddressRef.current.focus();
    };

    const handleClose = () => {
        setShowAddressSuggestion(false);
    };

    /**
     * After picking an address, send the address in the chat,
     * restore the input focus and hide the suggestions modal
     * @param {object} address
     */
    const handleClearAddressSuggestion = (address) => {
        setMessage(address.suggestion);
        updateIsTyping();
        restoreChatFocus();
        setShowAddressSuggestion(false);
    };

    /**
     * Send message to Aigo or connected Agent
     * @param {*} evt
     */
    const sendMessage = (evt) => {
        evt.preventDefault();
        const messageSanitized = inputSanitization(message, convId, featureFlags);
        if (messageSanitized && messageSanitized.trim()) {
            onSubmitMessage(messageSanitized);
            setMessage('');
        } else {
            setMessage('');
        }
    };

    useEffect(() => {
        // Make sure we have a value (user has entered something in input)
        if (debouncedSearchTerm) {
            const isoCountryData = getCountryCodeByCategoryPath(`/${country}`);
            if (isoCountryData?.countryCodeThreeDigit) {
                const params = {
                    query: debouncedSearchTerm,
                    country: isoCountryData?.countryCodeThreeDigit,
                };
                try {
                    QasClient.qasGet({}, 'search', params).then((address) => setAddressList(address.data));
                } catch (err) {
                    resetAutoSearch();
                }
            } else {
                resetAutoSearch();
            }
        } else {
            resetAutoSearch();
        }
    }, [debouncedSearchTerm]);

    // set chat input focus on chat open and chat address input focus
    useEffect(() => {
        if (lastMessageIndex?.responseMessage?.parameters?.inputType === 'adrs' && featureFlags['is-aigo-address-suggest-enabled'] && !isChatInputDisabled) { restoreChatFocus(); }
        if (lastMessageIndex?.responseMessage?.parameters?.inputType !== 'adrs' && forwardRef && !isChatInputDisabled) {
            forwardRef.current.focus();
        }
    }, [lastMessageIndex?.responseMessage?.parameters?.inputType === 'adrs']);

    /**
     * When the chat is maximized, restore chat input focus
     */
    useEffect(() => {
        if (!chatWidgetMinimized && forwardRef?.current?.focus) forwardRef.current.focus();
    }, [chatWidgetMinimized]);

    return (
        <form
            action="."
            onSubmit={sendMessage}
            data-testid="chat-input-form"
        >
            {
                lastMessageIndex?.responseMessage?.parameters?.inputType === 'adrs'
                    && featureFlags['is-aigo-address-suggest-enabled']
                    && AVAILABLE_COUNTRY_CODES.includes(country?.toLowerCase())
                    ? (
                        <>
                            {
                                showAddressSuggestion && message
                                && (
                                    <div data-testid="paper-test">
                                        <ClickAwayListener onClickAway={handleClose}>
                                            <Paper
                                                className={addressList && classList.menuListContainer}
                                            >
                                                <MenuList>
                                                    {getSuggestions(addressList, classList, handleClearAddressSuggestion, message, seed)}
                                                </MenuList>
                                            </Paper>
                                        </ClickAwayListener>
                                    </div>
                                )
                            }
                            <TextField
                                fullWidth
                                className={classList.chatInput}
                                placeholder="Enter message..."
                                value={message}
                                onChange={(e) => {
                                    setMessage(e.target.value);
                                    updateIsTyping();
                                    setSearchTerm(e.target.value);
                                    setShowAddressSuggestion(true);
                                }}
                                inputRef={chatAddressRef}
                                disabled={isChatInputDisabled}
                                InputProps={{
                                    inputProps: {
                                        'data-testid': 'chat-address',
                                    },
                                    disableUnderline: true,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={sendMessage}
                                                disabled={!message}
                                                className={classList.sendButton}
                                            >
                                                <SendIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </>
                    )
                    : (
                        <TextField
                            fullWidth
                            className={classList.chatInput}
                            placeholder="Enter message..."
                            value={message}
                            onChange={(e) => {
                                setMessage(e.target.value);
                                updateIsTyping();
                            }}
                            inputRef={forwardRef}
                            disabled={isChatInputDisabled}
                            InputProps={{
                                inputProps: {
                                    'data-testid': 'chat-input',
                                },
                                disableUnderline: true,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            data-testid="chat-send-button"
                                            aria-label="toggle password visibility"
                                            onClick={sendMessage}
                                            disabled={!message}
                                            className={classList.sendButton}
                                        >
                                            <SendIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    )
            }
        </form>
    );
};

ChatInput.propTypes = {
    classList: object.isRequired,
    featureFlags: object.isRequired,
    forwardRef: object.isRequired,
    isChatInputDisabled: bool.isRequired,
    chatWidgetMinimized: bool.isRequired,
    convId: string.isRequired,
    lastMessageIndex: object,
    // functions
    onSubmitMessage: func.isRequired,
    setIsTyping: func.isRequired,

};

ChatInput.defaultProps = {
    lastMessageIndex: {},
};

export default ChatInput;
